import { getCountries } from 'libphonenumber-js';
import { useMemo, useState } from 'react';

import type { SupportedCountry } from '@/constants/country';
import { isSupportedCountry } from '@/constants/country';
import { useCommonTranslation } from '@/hooks/useCommonTranslation';
import { defaultNS, useTranslation } from '@/lib/i18n';

import type { CountryOption } from '../-types';

export interface UseSelectCountryProps {
  value?: keyof typeof SupportedCountry;
  onChange: (countryCode?: keyof typeof SupportedCountry) => void;
  /** クリアボタン押下時の処理（任意項目でのみ渡す想定） */
  onClear?: () => void;
  disabled?: boolean;
}

export interface UseSelectCountryReturn {
  t: (key: 'searchPlaceholder' | 'noResultsMessage' | 'placeholder') => string;
  open: boolean;
  setOpen: (open: boolean) => void;
  searchText: string;
  setSearchText: (text: string) => void;
  countryOptions: CountryOption[];
  selectedCountry: CountryOption | undefined;
  handleCountryChange: (countryCode?: keyof typeof SupportedCountry) => void;
  onClear?: () => void;
  disabled?: boolean;
}

export function useSelectCountry(
  props: UseSelectCountryProps,
): UseSelectCountryReturn {
  const { value, onChange, onClear, disabled } = props;
  const { t } = useTranslation(defaultNS, {
    keyPrefix: 'component.selectCountry',
  });
  const tc = useCommonTranslation();
  const [open, setOpen] = useState(false);
  const [searchText, setSearchText] = useState('');

  const countryOptions: CountryOption[] = useMemo(
    () =>
      getCountries()
        .filter(isSupportedCountry)
        .map((code) => {
          return {
            key: code,
            label: tc(`country.${code}`),
            value: code,
          };
        }),
    [tc],
  );

  const selectedCountry = countryOptions.find((c) => c.value === value);

  const handleCountryChange = (countryCode?: keyof typeof SupportedCountry) => {
    onChange(countryCode);
    if (countryCode) {
      setOpen(false);
    }
  };

  return {
    t,
    open,
    setOpen,
    searchText,
    setSearchText,
    countryOptions,
    selectedCountry,
    handleCountryChange,
    onClear,
    disabled,
  };
}
